import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { LoadingSkeleton } from '../ui/LoadingSkeleton';

interface PortalGuardProps {
  /** Allowed roles for this route */
  roles: string[];
  children: ReactNode;
  /** Where to redirect unauthenticated users */
  loginPath?: string;
}

export function PortalGuard({
  roles,
  children,
  loginPath = '/login',
}: PortalGuardProps) {
  const { user, isLoading, isAuthenticated } = useAuth();
  const location = useLocation();

  // ── Loading ───────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[var(--bg-base)]">
        <div className="w-full max-w-sm px-4">
          <LoadingSkeleton variant="card" count={1} />
        </div>
      </div>
    );
  }

  // ── Not authenticated ─────────────────────────────────────────────────────
  if (!isAuthenticated || !user) {
    return <Navigate to={loginPath} state={{ from: location }} replace />;
  }

  // ── Wrong role ────────────────────────────────────────────────────────────
  if (!roles.includes(user.role)) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[var(--bg-base)] px-4">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-500/15 flex items-center justify-center">
            <svg
              className="w-8 h-8 text-[var(--brand-danger)]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
              />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-[var(--text-primary)] mb-1">
            Access Denied
          </h2>
          <p className="text-sm text-[var(--text-muted)] mb-6">
            You do not have permission to access this page. Please contact your
            administrator if you believe this is an error.
          </p>
          <a
            href="/"
            className="inline-flex items-center justify-center min-h-[44px] px-6 py-2.5 text-sm font-medium rounded-[var(--radius-md)] bg-gradient-to-r from-[var(--brand-primary)] to-[var(--brand-accent)] text-white hover:brightness-110 transition-all"
          >
            Go to Home
          </a>
        </div>
      </div>
    );
  }

  // ── Authorized ────────────────────────────────────────────────────────────
  return <>{children}</>;
}
